import React from 'react';
import { motion } from 'framer-motion';
import Footer from '../../components/Footer';
import DiplomaImg from "../../assets/courses/diploma.gif";
import { Link } from 'react-router-dom';
import {
    LocalPharmacy as PharmacyIcon,
    School as SchoolIcon,
    Restaurant as FoodIcon,
    Cake as CakeIcon,
    MedicalServices as MedicalIcon,
    Accessibility as RehabilitationIcon,
    Healing as NursingIcon
} from '@mui/icons-material';

// Additional imports for enhanced design
import {
    CheckCircle,
    TrendingUp,
    Globe,
    Award
} from 'lucide-react';

// Organized by schools
const diplomaSchools = [
    {
        name: 'School of Hotel Management and Tourism',
        icon: SchoolIcon,
        courses: [
            {
                name: 'Diploma in Bakery & Confectionery',
                description: 'The Diploma in Bakery and Confectionery is an extensive 1 ½ year program aimed at equipping students for fulfilling careers in the culinary and hospitality sectors.',
                path: 'diploma-courses/best-bakery-and-confectionery-college-in-dehradun-uttarakhand',
                icon: CakeIcon,
                duration: '1.5 Years',
                eligibility: '10+2 from any stream',
                features: [
                    'Advanced Baking Techniques',
                    'Pastry Design Skills',
                    'Chocolate Artistry',
                    'Dessert Presentation',
                    'Bakery Business Management'
                ],
                careerOpportunities: [
                    'Pastry Chef',
                    'Bakery Manager',
                    'Cake Designer',
                    'Confectionery Instructor',
                    'Dessert Consultant'
                ]
            },
            {
                name: 'Diploma in Food Production',
                description: 'The Diploma in Food Production is an extensive 1 ½ year program aimed at equipping students for fulfilling careers in the culinary and hospitality sectors.',
                path: 'diploma-courses/diploma-in-food-production',
                icon: FoodIcon,
                duration: '1.5 Years',
                eligibility: '10+2 from any stream',
                features: [
                    'Professional Culinary Techniques',
                    'International Cuisine Exploration',
                    'Kitchen Management Skills',
                    'Nutrition and Food Safety',
                    'Practical Cooking Workshops'
                ],
                careerOpportunities: [
                    'Commis Chef',
                    'Kitchen Supervisor',
                    'Culinary Instructor',
                    'Food Production Manager',
                    'Catering Coordinator'
                ]
            }
        ]
    },
    {
        name: 'School of Pharmacy',
        icon: PharmacyIcon,
        courses: [
            {
                name: 'Diploma in Pharmacy (D.Pharma)',
                description: 'School of Pharmacy is running a two-year full-time diploma course divided into two annual sessions approved by Pharmacy Council of India, New Delhi.',
                path: 'diploma-courses/diploma-in-pharmacy',
                icon: PharmacyIcon,
                duration: '2 Years',
                eligibility: '10+2 with Science',
                features: [
                    'Comprehensive Pharmaceutical Curriculum',
                    'Industry-Recognized Certification',
                    'Hands-on Laboratory Training',
                    'Clinical Pharmacy Exposure',
                    'Research Opportunities'
                ],
                careerOpportunities: [
                    'Pharmacy Technician',
                    'Hospital Pharmacist',
                    'Retail Pharmacy Manager',
                    'Pharmaceutical Sales Representative',
                    'Drug Regulatory Affairs Specialist'
                ]
            }
        ]
    },
    {
        name: 'School of Paramedical Sciences',
        icon: MedicalIcon,
        courses: [
            {
                name: 'Diploma in Dialysis',
                description: 'The Diploma in Dialysis program provides comprehensive training for students to become skilled dialysis technicians capable of assisting patients with kidney disorders.',
                path: 'diploma-courses/diploma-in-dialysis',
                icon: MedicalIcon,
                duration: '2 Years',
                eligibility: '10+2 with Science',
                features: [
                    'Dialysis Machine Operation',
                    'Patient Care Techniques',
                    'Renal Disorders Knowledge',
                    'Clinical Internship',
                    'Emergency Response Training'
                ],
                careerOpportunities: [
                    'Dialysis Technician',
                    'Renal Care Specialist',
                    'Dialysis Unit Coordinator',
                    'Home Dialysis Technician',
                    'Medical Equipment Technician'
                ]
            }
        ]
    },
    {
        name: 'Ashtavakra School of Rehabilitation',
        icon: RehabilitationIcon,
        courses: [
            {
                name: 'Diploma in Early Child Special Education (Hearing Impairment)',
                description: 'This specialized diploma provides training in educational techniques and interventions for young children with hearing impairments.',
                path: 'diploma-courses/diploma-in-early-child-special-education-hearing-impairment',
                icon: RehabilitationIcon,
                duration: '1 Year',
                eligibility: '10+2 from any stream',
                features: [
                    'Sign Language Training',
                    'Hearing Aid Management',
                    'Educational Adaptations',
                    'Child Development Knowledge',
                    'Inclusive Teaching Methods'
                ],
                careerOpportunities: [
                    'Special Education Teacher',
                    'Early Intervention Specialist',
                    'Inclusive School Coordinator',
                    'Hearing Support Assistant',
                    'Educational Therapist'
                ]
            },
            {
                name: 'Diploma in Early Rehabilitation Therapy',
                description: 'This program focuses on therapeutic interventions for children requiring early rehabilitation support and services.',
                path: 'diploma-courses/diploma-in-early-rehabilitation-therapy',
                icon: RehabilitationIcon,
                duration: '1 Year',
                eligibility: '10+2 from any stream',
                features: [
                    'Early Intervention Techniques',
                    'Developmental Assessment',
                    'Family Support Training',
                    'Therapeutic Play Methods',
                    'Multi-disciplinary Approach'
                ],
                careerOpportunities: [
                    'Early Intervention Therapist',
                    'Rehabilitation Assistant',
                    'Child Development Specialist',
                    'Special Needs Coordinator',
                    'Therapy Center Associate'
                ]
            },
            {
                name: 'Diploma in Rehabilitation (Mental Retardation)',
                description: 'This diploma provides specialized training in supporting individuals with intellectual disabilities through rehabilitation techniques.',
                path: 'diploma-courses/diploma-in-rehabilitation-mental-retardation',
                icon: RehabilitationIcon,
                duration: '1 Year',
                eligibility: '10+2 from any stream',
                features: [
                    'Cognitive Development Support',
                    'Behavior Management Techniques',
                    'Life Skills Training',
                    'Inclusive Education Practices',
                    'Assistive Technology Usage'
                ],
                careerOpportunities: [
                    'Rehabilitation Counselor',
                    'Special Education Teacher',
                    'Vocational Trainer',
                    'Community Support Worker',
                    'Residential Care Coordinator'
                ]
            },
            {
                name: 'Diploma in Prosthetics and Orthotics',
                description: 'This program trains students in designing, creating, and fitting prosthetic and orthotic devices to enhance mobility and function.',
                path: 'diploma-courses/diploma-in-prosthetics-and-orthotics',
                icon: RehabilitationIcon,
                duration: '2 Years',
                eligibility: '10+2 with Science',
                features: [
                    'Prosthetic Device Design',
                    'Orthotic Fabrication',
                    'Biomechanics Knowledge',
                    'Patient Assessment Skills',
                    'Rehabilitation Coordination'
                ],
                careerOpportunities: [
                    'Prosthetic Technician',
                    'Orthotic Specialist',
                    'Rehabilitation Equipment Provider',
                    'Assistive Device Consultant',
                    'Mobility Aid Designer'
                ]
            },
            {
                name: 'Diploma in Hearing Language and Speech',
                description: 'This diploma focuses on assessment and intervention techniques for individuals with hearing, language, and speech disorders.',
                path: 'diploma-courses/diploma-in-hearing-language-and-speech',
                icon: RehabilitationIcon,
                duration: '1.5 Years',
                eligibility: '10+2 from any stream',
                features: [
                    'Hearing Assessment Methods',
                    'Speech Therapy Techniques',
                    'Language Development Skills',
                    'Communication Disorder Knowledge',
                    'Therapeutic Intervention Planning'
                ],
                careerOpportunities: [
                    'Speech Therapy Assistant',
                    'Hearing Clinic Associate',
                    'Language Development Specialist',
                    'Communication Disorder Technician',
                    'School Speech Program Coordinator'
                ]
            },
            {
                name: 'Diploma in Indian Sign Language',
                description: 'This specialized diploma trains students in Indian Sign Language proficiency and interpretation techniques.',
                path: 'diploma-courses/diploma-in-indian-sign-language',
                icon: RehabilitationIcon,
                duration: '1 Year',
                eligibility: '10+2 from any stream',
                features: [
                    'Indian Sign Language Mastery',
                    'Deaf Culture Understanding',
                    'Interpretation Techniques',
                    'Communication Strategy Development',
                    'Community Engagement Skills'
                ],
                careerOpportunities: [
                    'Sign Language Interpreter',
                    'Deaf Education Assistant',
                    'Inclusive Communication Specialist',
                    'Community Support Worker',
                    'Accessibility Coordinator'
                ]
            }
        ]
    },
    {
        name: 'School of Nursing',
        icon: NursingIcon,
        courses: [
            {
                name: 'General Nursing and Midwifery (GNM)',
                description: 'The GNM program provides comprehensive training in nursing care and midwifery skills for a career in healthcare settings.',
                path: 'diploma-courses/general-nursing-and-midwifery',
                icon: NursingIcon,
                duration: '3 Years',
                eligibility: '10+2 with Biology',
                features: [
                    'Clinical Nursing Skills',
                    'Midwifery Training',
                    'Patient Care Management',
                    'Healthcare Ethics',
                    'Medical Emergency Response'
                ],
                careerOpportunities: [
                    'Staff Nurse',
                    'Midwife',
                    'Community Health Nurse',
                    'Healthcare Facility Supervisor',
                    'Maternal Health Specialist'
                ]
            },
            {
                name: 'Diploma in Nursing Assistant',
                description: 'This program trains students to provide basic nursing care and assist registered nurses in various healthcare settings.',
                path: 'diploma-courses/diploma-in-nursing-assistant',
                icon: NursingIcon,
                duration: '1 Year',
                eligibility: '10+2 from any stream',
                features: [
                    'Basic Patient Care',
                    'Vital Sign Monitoring',
                    'Medical Equipment Handling',
                    'Infection Control Practices',
                    'Healthcare Communication Skills'
                ],
                careerOpportunities: [
                    'Nursing Assistant',
                    'Patient Care Technician',
                    'Healthcare Aide',
                    'Home Health Assistant',
                    'Elder Care Specialist'
                ]
            }
        ]
    }
];

const DiplomaCourses = () => {
    return (
        <div className='w-full relative font-poppins bg-gradient-to-br from-green-50 to-white min-h-screen'>
            {/* Hero Section */}
            <motion.div
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="relative pt-[100px] md:pt-[150px] text-center"
            >
                <h2 className="mb-4 text-4xl md:text-6xl tracking-tight font-bold text-[#0e4d38]">
                    Diploma Courses
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto px-4">
                    Empowering Future Professionals through Specialized Diploma Programs
                </p>
            </motion.div>

            {/* Main Content */}
            <div className='w-11/12 mx-auto mb-[50px] relative z-10'>
                {/* Course Overview Section */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.2 }}
                    className="flex flex-col gap-5 md:gap-[100px] md:flex-row justify-between items-center my-16"
                >
                    {/* Diploma Image */}
                    <motion.div
                        whileHover={{
                            scale: 1.05,
                            rotate: 2,
                            transition: { duration: 0.3 }
                        }}
                        className="relative rounded-2xl overflow-hidden shadow-2xl"
                    >
                        <img
                            src={DiplomaImg}
                            className="w-[400px] h-[400px] max-h-[450px] object-contain md:object-cover"
                            alt="Diploma Courses"
                        />
                    </motion.div>

                    {/* Description Section */}
                    <div className="w-full md:w-1/2 space-y-6">
                        <h3 className="text-3xl font-bold text-[#0e4d38] mb-4">
                            Why Choose Our Diploma Programs?
                        </h3>
                        <div className="space-y-4 bg-white p-6 rounded-lg shadow-md">
                            {[
                                {
                                    icon: <CheckCircle className="text-green-500 mr-3" />,
                                    text: "Comprehensive Industry-Aligned Curriculum"
                                },
                                {
                                    icon: <TrendingUp className="text-green-600 mr-3" />,
                                    text: "Career-Focused Skill Development"
                                },
                                {
                                    icon: <Globe className="text-green-700 mr-3" />,
                                    text: "Global Learning Standards"
                                },
                                {
                                    icon: <Award className="text-green-800 mr-3" />,
                                    text: "Recognized Certifications"
                                }
                            ].map((item, index) => (
                                <div key={index} className="flex items-center">
                                    {item.icon}
                                    <span className="text-gray-700">{item.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Schools Section */}
                <div className="space-y-16">
                    {diplomaSchools.map((school, schoolIndex) => (
                        <motion.div
                            key={schoolIndex}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1 * schoolIndex }}
                            className="bg-white rounded-xl shadow-lg overflow-hidden"
                        >
                            {/* School Header */}
                            <div className="bg-gradient-to-r from-green-600 to-green-800 p-6">
                                <div className="flex items-center space-x-4">
                                    <school.icon sx={{ color: 'white', fontSize: 32 }} />
                                    <h3 className="text-white text-2xl font-bold">{school.name}</h3>
                                </div>
                            </div>

                            {/* Courses Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                                {school.courses.map((course, index) => (
                                    <motion.div
                                        key={index}
                                        whileHover={{ y: -5 }}
                                        className="border border-green-100 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
                                    >
                                        <div className="p-6">
                                            <div className="flex items-center mb-4">
                                                <course.icon sx={{ color: '#0e4d38', fontSize: 28, mr: 2 }} />
                                                <h4 className="text-lg font-semibold text-green-800">{course.name}</h4>
                                            </div>
                                            <p className="text-gray-600 text-sm mb-4">{course.description}</p>
                                            
                                            <div className="mb-4">
                                                <div className="text-sm text-green-700 font-medium mb-1">Duration: {course.duration}</div>
                                                <div className="text-sm text-green-700 font-medium">Eligibility: {course.eligibility}</div>
                                            </div>
                                            
                                            <ul className="text-sm text-gray-600 mb-4 space-y-1">
                                                {course.features.slice(0, 3).map((feature, idx) => (
                                                    <li key={idx} className="flex items-start">
                                                        <CheckCircle className="text-green-500 mt-0.5 mr-2 flex-shrink-0" size={16} />
                                                        <span>{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="px-6 pb-4">
                                            <Link to={`/programmes/${course.path}`}>
                                                <button className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded transition-colors">
                                                    View Details
                                                </button>
                                            </Link>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default DiplomaCourses;